import { useState, useEffect } from "react";
import {
  getInitialTheme,
  getEffectiveTheme,
  applyTheme,
  setupThemeChangeListener,
  Theme,
} from "../utils/themeUtils";

type ThemeListener = () => void;

let currentTheme: Theme = getInitialTheme();
const listeners = new Set<ThemeListener>();
let removeSystemThemeListener: (() => void) | null = null;

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

const ensureSystemThemeListener = () => {
  if (currentTheme === "auto" && !removeSystemThemeListener) {
    removeSystemThemeListener = setupThemeChangeListener(() => {
      applyTheme("auto");
      notifyListeners();
    });
  } else if (currentTheme !== "auto" && removeSystemThemeListener) {
    removeSystemThemeListener();
    removeSystemThemeListener = null;
  }
};

if (typeof document !== "undefined") {
  applyTheme(currentTheme);
}
ensureSystemThemeListener();

const subscribe = (listener: ThemeListener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const updateTheme = (newTheme: Theme) => {
  if (currentTheme === newTheme) {
    return;
  }

  currentTheme = newTheme;
  try {
    localStorage.setItem("theme", newTheme);
  } catch (error) {
    // ignore write errors (e.g., Safari private mode)
  }
  applyTheme(newTheme);
  ensureSystemThemeListener();
  notifyListeners();
};

const toggleThemeValue = () => {
  const newTheme =
    currentTheme === "light"
      ? "dark"
      : currentTheme === "dark"
        ? "auto"
        : "light";
  updateTheme(newTheme);
};

export const useTheme = () => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => {
      forceUpdate((value) => value + 1);
    };

    const unsubscribe = subscribe(listener);
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    theme: currentTheme,
    setTheme: updateTheme,
    toggleTheme: toggleThemeValue,
    effectiveTheme: getEffectiveTheme(currentTheme),
  };
};
